import { RefObject, useEffect, useRef } from 'react';
import { focusReturnTarget, ReturnFocusRequest } from '../helpers/fieldFocusHelper';
import { findScreenEntryTarget, SCREEN_CONTENT_SELECTOR } from '../helpers/screenEntryFocus';

const HEADING_SELECTOR = 'header h1, header h2, header h3, header h4, header h5, header h6';

const ENTRY_WAIT_MS = 3000;
const WATCHED_ATTRIBUTES = ['data-entry-pending', 'disabled', 'hidden', 'aria-hidden'];

type EntryAttempt = 'done' | 'retry';

const focusQuietly = (element: HTMLElement) => {
  if (!element.hasAttribute('tabindex')) element.tabIndex = -1;
  element.focus({ preventScroll: true });
};

const isFocusOnNothing = (layout: HTMLElement) => {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  return layout.querySelector('header')?.contains(active) ?? false;
};

const attemptScreenEntry = (layout: HTMLElement): EntryAttempt => {
  if (!document.hasFocus()) return 'done';
  if (!isFocusOnNothing(layout)) {
    return layout.contains(document.activeElement) ? 'done' : 'retry';
  }
  const content = layout.querySelector<HTMLElement>(SCREEN_CONTENT_SELECTOR) ?? layout;
  const target = findScreenEntryTarget(content);
  if (target) {
    target.focus();
    return 'done';
  }
  // Holds focus somewhere a screen reader announces while the screen is still loading.
  const heading = layout.querySelector<HTMLElement>(HEADING_SELECTOR);
  if (heading && heading !== document.activeElement) focusQuietly(heading);
  return 'retry';
};

export const focusScreenEntryWhenReady = (layout: HTMLElement) => {
  if (attemptScreenEntry(layout) === 'done') return () => undefined;

  let frame = 0;
  const observer = new MutationObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (attemptScreenEntry(layout) === 'done') stop();
    });
  });
  const stop = () => {
    cancelAnimationFrame(frame);
    clearTimeout(timeout);
    observer.disconnect();
    document.removeEventListener('keydown', stop, true);
    document.removeEventListener('pointerdown', stop, true);
  };

  // The body, not the layout: a closing menu or dialog lives in a portal outside it.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: WATCHED_ATTRIBUTES,
  });
  document.addEventListener('keydown', stop, true);
  document.addEventListener('pointerdown', stop, true);
  const timeout = setTimeout(stop, ENTRY_WAIT_MS);
  return stop;
};

export const useFocusOnNavigation = (
  layoutRef: RefObject<HTMLElement | null>,
  pathname: string,
  takeReturnFocusRequest: () => ReturnFocusRequest | undefined,
) => {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    const returnFocusRequest = takeReturnFocusRequest();
    let stopWaiting: (() => void) | undefined;
    const frame = requestAnimationFrame(() => {
      const layout = layoutRef.current;
      if (!layout || !document.hasFocus()) return;
      if (returnFocusRequest && focusReturnTarget(layout, returnFocusRequest)) return;
      stopWaiting = focusScreenEntryWhenReady(layout);
    });
    return () => {
      cancelAnimationFrame(frame);
      stopWaiting?.();
    };
  }, [pathname]);
};
